import { createApi } from '@reduxjs/toolkit/query/react';

import { apiSahCompanyExternalContributions } from '~constants/api';

import { rtkBaseQueryWrapper, unwrap } from '~libs/axiosWrapper';

/**
 * One contribution rate the import wrote, in either the created or the updated list.
 *
 * `id` is a composite key (`client_id|category_id|funding_source_id|start_date`) because TableView
 * needs a unique `id` on every row. `start_date` / `end_date` are bare calendar dates (YYYY-MM-DD),
 * `end_date` is null for an open-ended rate.
 */
export interface ImportContributionItem {
  id: string;
  client_id: number;
  client_name: string;
  category_name: string;
  // The budget type the rate applies to.
  funding_source_name: string;
  contrib_proportion: number;
  start_date: string;
  end_date: string | null;
}

/**
 * A contribution rate the import fetched but did not write, with the first reason it could not be
 * written. Values are as Services Australia sent them. `client_name` is null whenever no TurnPoint
 * client matched, and `contribution_type` is the Services Australia type code.
 */
export interface ImportContributionSkipped {
  care_recipient_id: string | null;
  client_name: string | null;
  category_name: string | null;
  contribution_type: string | null;
  contrib_proportion: number | string | null;
  start_date: string | null;
  end_date: string | null;
  reason: string;
}

/**
 * The `data` payload of `POST .../companies/external/contributions/import`, after `unwrap()`.
 * `truncated` means the server hit its defensive page cap, not that anything failed.
 *
 * `window` is the date range the SERVER used. It is fixed server-side (the last 12 months and the
 * next 6 months, the same as the budgets import) and echoed back so the result can name real dates.
 * Both are bare calendar dates (YYYY-MM-DD).
 */
export interface ImportContributionsResponse {
  window: { effective_date_from: string; effective_date_to: string };
  fetched: number;
  truncated: boolean;
  created: number;
  updated: number;
  created_items: ImportContributionItem[];
  updated_items: ImportContributionItem[];
  skipped: ImportContributionSkipped[];
}

/**
 * Company-scoped Support at Home contributions actions (TAV-3421).
 *
 * A slice of its own, like sahCompanyServicesApi, because the route lives under `/v2/sah/companies/...`.
 *
 * This slice MUST stay registered in `supportAtHomeApis` (reducers/index.ts) - store.ts reads that
 * array for both reducers and middleware, and without it the hook silently no-ops.
 */
export const sahCompanyContributionsApi = createApi({
  reducerPath: 'sahCompanyContributionsApi',
  baseQuery: rtkBaseQueryWrapper({ baseUrl: apiSahCompanyExternalContributions }),
  tagTypes: ['sah-company-contributions'],
  endpoints: (builder) => ({
    importContributions: builder.mutation<ImportContributionsResponse, void>({
      query: () => ({ url: '/import', method: 'POST', body: {} }),
      // No invalidatesTags: the modal's onClose calls the parent's refetch() instead.
      transformResponse: (payload) => unwrap<ImportContributionsResponse>(payload),
    }),
  }),
});

export const { useImportContributionsMutation } = sahCompanyContributionsApi;
